// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving event groups associated with an asset.
 * Provides functionality to fetch multi-step processes like shipping
 * or maintenance routines, with optional date range filtering and
 * automatic chronological sorting.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { EventGroup, eventGroupSchema } from '@/asset/api/types';
import { DateTime } from 'luxon';

/**
 * Compares two event groups for chronological sorting.
 * Groups are sorted by their earliest event date (minimumDate).
 * Groups with no minimum date are considered to be "later" than
 * groups with dates for consistent sorting behavior.
 *
 * @param group - First event group to compare
 * @param other - Second event group to compare
 * @returns Negative if other is earlier, positive if group is earlier, zero if equal
 */
function compareEventGroups(group: EventGroup, other: EventGroup) {
  if (group.minimumDate === null) {
    return 1;
  }

  if (other.minimumDate === null) {
    return -1;
  }

  return other.minimumDate.toMillis() - group.minimumDate.toMillis();
}

/**
 * Schema for validating the API response when fetching event groups.
 * Transforms the response to provide a more intuitive structure with
 * the event groups array separated from metadata. Groups are automatically
 * sorted chronologically using the compareEventGroups function.
 */
const eventGroupsResponseSchema = z
  .object({
    data: z.array(eventGroupSchema).nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    eventGroups: data?.slice().sort(compareEventGroups) ?? [],
  }));

type EventGroupsResponse = z.infer<typeof eventGroupsResponseSchema>;

/**
 * Parameters for filtering event groups by date range.
 * Both start and end dates are optional, allowing flexible
 * range queries.
 *
 * @typedef {Object} EventGroupsParameters
 * @property {DateTime} [start] - Optional start date for filtering groups
 * @property {DateTime} [end] - Optional end date for filtering groups
 */
export interface EventGroupsParameters {
  start?: DateTime;
  end?: DateTime;
}

/**
 * Retrieves event groups associated with an asset.
 * This function handles the API call to fetch multi-step processes
 * and validates the response data using Zod schemas. It supports
 * optional date range filtering and automatically sorts groups
 * chronologically for consistent presentation.
 *
 * @param assetId - Unique identifier of the asset to fetch event groups for
 * @param parameters - Optional object containing date range filters
 * @returns Promise resolving to the sorted list of event groups and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getEventGroups(
  assetId: string,
  parameters?: EventGroupsParameters
): Promise<EventGroupsResponse> {
  const url = `/v1/asset/${assetId}/event-group`;
  const response = await api.get(url, {
    params: {
      start: parameters?.start?.toISO(),
      end: parameters?.end?.toISO(),
    },
  });

  const result = await parseResponse(eventGroupsResponseSchema, response.data, {
    schemaName: 'EventGroups',
    endpoint: url,
  });

  return result;
}
